import React from "react";
import { motion } from "framer-motion";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import Preloader from "./LoadingAnimations/Preloader";

const getWorkspaceGradient = (isTarget = false) => {
  return isTarget ? "from-emerald-500 via-teal-500 to-cyan-600" : "from-blue-500 via-indigo-500 to-purple-600";
};

const WorkspaceIcon = ({ workspace, isTarget = false }) => {
  const logo = workspace?.square_logo || workspace?.image_logo || workspace?.image_favicon;
  const initials = typeof workspace?.name === "string" ? workspace.name.trim().charAt(0).toUpperCase() || "?" : "?";
  const [imageFailed, setImageFailed] = React.useState(false);

  React.useEffect(() => {
    setImageFailed(false);
  }, [workspace?.id, workspace?.name, logo]);

  const hasValidLogo = Boolean(logo && logo.trim()) && !imageFailed;

  return hasValidLogo ? (
    <motion.div
      className="relative h-16 w-16 overflow-hidden rounded-full shadow-lg ring-2 ring-white/30"
      whileHover={{ scale: 1.05 }}
      transition={{ type: "spring", stiffness: 400, damping: 10 }}>
      <img
        src={logo}
        alt={workspace?.name || "Workspace"}
        className="h-full w-full object-cover"
        onError={() => setImageFailed(true)}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-black/10" />
    </motion.div>
  ) : (
    <motion.div
      className={`relative h-16 w-16 bg-gradient-to-br ${getWorkspaceGradient(isTarget)} flex items-center justify-center rounded-full text-xl font-bold text-white shadow-lg ring-2 ring-white/30`}
      whileHover={{ scale: 1.05 }}
      transition={{ type: "spring", stiffness: 400, damping: 10 }}>
      {initials}
      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/20 via-transparent to-transparent" />
    </motion.div>
  );
};
const WorkspaceSwitchingOverlay = ({ isVisible, currentWorkspace, targetWorkspace }) => {
  if (!isVisible) return null;

  const isWorkspaceSwitching = currentWorkspace && targetWorkspace;

  return !isWorkspaceSwitching ? (
    <motion.div
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-white">
      <Preloader text={window.location.pathname.includes("authorization") ? "Authentication..." : "Loading..."} />
    </motion.div>
  ) : (
    <motion.div
      initial={{ opacity: 0.5 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-slate-50 via-gray-100 to-slate-200 font-sans">
      <div className="absolute inset-0 z-0 h-full w-full bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#000_70%,transparent_100%)]" />
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -right-40 -top-40 h-80 w-80 rounded-full bg-gradient-to-br from-blue-400/20 to-purple-600/20 blur-3xl"
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-gradient-to-tr from-emerald-400/20 to-cyan-600/20 blur-3xl"
          animate={{ rotate: -360 }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        />
      </div>

      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.25, 0.8, 0.25, 1] }}
        className="relative mx-4 w-full max-w-md rounded-3xl border border-white/40 bg-white/90 p-8 text-center shadow-2xl backdrop-blur-xl">
        <div className="pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-br from-white/50 via-transparent to-gray-100/30" />

        <motion.h2
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4, ease: "easeOut" }}
          className="relative mb-12 bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-2xl font-bold text-transparent">
          {isWorkspaceSwitching ? "Switching Workspace" : "Preparing Your Environment"}
        </motion.h2>

        <div className="relative mb-8 flex items-center justify-center gap-8">
          <motion.div
            initial={{ scale: 1, x: 0 }}
            animate={{ scale: 0.95, x: 10 }}
            transition={{ delay: 0.3, duration: 0.8, ease: "easeInOut" }}
            className="flex flex-col items-center gap-3">
            <WorkspaceIcon workspace={currentWorkspace} />
            <motion.div
              initial={{ opacity: 1 }}
              animate={{ opacity: 0.7 }}
              transition={{ delay: 0.4, duration: 0.4 }}
              className="max-w-20 truncate text-sm font-semibold text-gray-600">
              {currentWorkspace?.name || "Current"}
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5, duration: 0.4 }}
            className="relative flex items-center">
            <motion.div className="absolute inset-0 flex items-center justify-center">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="mx-1 h-1.5 w-1.5 rounded-full bg-gradient-to-r from-blue-500 to-purple-500"
                  animate={{
                    x: [0, 25, 0],
                    opacity: [0, 1, 0],
                    scale: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 1.8,
                    repeat: Infinity,
                    delay: i * 0.3,
                    ease: "easeInOut",
                  }}
                />
              ))}
            </motion.div>

            <motion.div
              animate={{ x: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="z-10 text-gray-500">
              <ArrowRightIcon
                className="h-7 w-7"
                strokeWidth={2}
              />
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ scale: 0.8, x: -20 }}
            animate={{ scale: 1, x: 0 }}
            transition={{ delay: 0.6, duration: 0.8, ease: "easeInOut" }}
            className="flex flex-col items-center gap-3">
            <motion.div
              animate={{
                boxShadow: ["0 0 0 0 rgba(16, 185, 129, 0.4)", "0 0 0 12px rgba(16, 185, 129, 0)", "0 0 0 0 rgba(16, 185, 129, 0)"],
              }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
              className="rounded-full">
              <WorkspaceIcon
                workspace={targetWorkspace}
                isTarget
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0.6 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.3 }}
              className="max-w-20 truncate text-sm font-semibold text-gray-800">
              {targetWorkspace?.name || "Target"}
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.4 }}
          className="relative space-y-5">
          <div className="flex justify-center gap-2">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0.3, scale: 0.8 }}
                animate={{
                  opacity: [0.3, 1, 0.3],
                  scale: [0.8, 1.2, 0.8],
                }}
                transition={{
                  delay: i * 0.2,
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="h-2.5 w-2.5 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 shadow-sm"
              />
            ))}
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default WorkspaceSwitchingOverlay;
